import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useReaderCredits } from "../contexts/ReaderCreditContext";
import { useReaderFunnel } from "../contexts/ReaderFunnelContext";
import { useNotification } from "../contexts/NotificationContext";
import ReaderNav from "../components/ReaderNav";
import ReaderMobileBottomNav from "../components/ReaderMobileBottomNav";
import UniversalFooter from "../components/UniversalFooter";
import AuthorProfileModal from "../components/Author/AuthorProfileModal";
import AboutAuthorModal from "../components/AboutAuthorModal";
import AboutBookModal from "../components/AboutBookModal";
import AudiaChatModal from "../components/AudiaChatModal";
import AskAudiaHelpButton from "../components/AskAudiaHelpButton";
import { Play, BookOpen, Info, User, Archive, ArchiveRestore, ShoppingCart } from 'lucide-react';
import { BACKEND_URL as backendUrl } from '@/config/api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ReaderLibraryPage = () => {
  const { user, logout, getHomePage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { balance } = useReaderCredits();
  const { setShowCreditExpiredModal, setShowPricingModal, isBetaTester } = useReaderFunnel();
  const { notifyBilling } = useNotification();

  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  
  // Journey stats for "Your Journey So Far" card
  const [journeyStats, setJourneyStats] = useState(null);
  
  // Modal state for bookmark options
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [selectedManuscript, setSelectedManuscript] = useState(null);
  
  // Modal state for book details
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsManuscript, setDetailsManuscript] = useState(null);
  
  // Modal state for archive confirmation
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [bookToArchive, setBookToArchive] = useState(null);
  
  // Modal state for archive success
  const [showArchiveSuccessModal, setShowArchiveSuccessModal] = useState(false);
  const [archiveResult, setArchiveResult] = useState(null);
  
  // Modal state for author profile (independent authors)
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  
  // Modal state for managed author (publisher authors)
  const [showManagedAuthorModal, setShowManagedAuthorModal] = useState(false);
  const [selectedManagedAuthorId, setSelectedManagedAuthorId] = useState(null);
  
  // Modal state for remove confirmation
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [bookToRemove, setBookToRemove] = useState(null);
  
  // Modal state for Ask Audia
  const [showAudiaChatModal, setShowAudiaChatModal] = useState(false);
  const [audiaChatManuscript, setAudiaChatManuscript] = useState(null);
  
  // Modal state for About Book (when clicking on author's other titles)
  const [showAboutBookModal, setShowAboutBookModal] = useState(false);
  const [aboutBookData, setAboutBookData] = useState(null);

  // Handler for when a book is selected from About Author modal
  const handleSelectBookFromAuthor = (selectedBook) => {
    console.log('[ReaderLibraryPage] Book selected from author titles:', selectedBook.title);
    setShowManagedAuthorModal(false);
    setSelectedManagedAuthorId(null);
    setAboutBookData(selectedBook);
    setShowAboutBookModal(true);
  };

  useEffect(() => {
    // Check if user has reader role, otherwise redirect
    if (user && !user.roles?.includes('reader')) {
      navigate(getHomePage(user));
      return;
    }
    
    fetchLibrary();
    fetchJourneyStats();
  }, [user, navigate, getHomePage, showArchived]);
  
  // Handle auto-open recap from WelcomeBackModal navigation
  useEffect(() => {
    if (location.state?.autoOpenRecap && location.state?.showRecapFor && library.length > 0) {
      const manuscriptId = location.state.showRecapFor;
      const manuscript = library.find(m => m.manuscriptId === manuscriptId);
      
      if (manuscript) {
        console.log('[My Library] Auto-opening recap for:', manuscript.title);
        setSelectedManuscript(manuscript);
        setShowResumeModal(true);
        
        // Clear the state to prevent re-triggering
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, library, navigate]);
  
  // Refetch library when component becomes visible (returning from player)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[My Library] Page visible again, refetching library...');
        fetchLibrary();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const fetchLibrary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL

      // When showArchived is true, fetch ONLY archived books
      const url = `${backendUrl}/api/reader/library${showArchived ? '?archived_only=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch library');
      }

      const data = await response.json();
      const libraryData = data.library || [];
      
      // OVERRIDE bookmarks with localStorage data (single source of truth)
      let userId = 'anonymous';
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.user_id || payload.sub || 'anonymous';
        } catch (e) {
          console.warn('Could not extract user ID from token');
        }
      }
      
      // For each manuscript, check localStorage for bookmark
      const enrichedData = libraryData.map(manuscript => {
        const bookmarkKey = `newaudia_bookmark_${userId}_${manuscript.manuscriptId}`;
        const localBookmark = localStorage.getItem(bookmarkKey);
        
        if (localBookmark) {
          try {
            const parsed = JSON.parse(localBookmark);
            console.log(`[Library] Using localStorage bookmark for ${manuscript.title}:`, parsed);
            // Override with localStorage data (include timestamp for 24-hour check)
            manuscript.bookmark = {
              chapterIndex: parsed.chapterIndex,
              chunkIndex: parsed.chunkIndex,
              positionSeconds: parsed.positionSeconds,
              timestamp: parsed.timestamp  // For 24-hour bookmark age check
            };
          } catch (e) {
            console.error('Error parsing localStorage bookmark:', e);
          }
        }
        
        return manuscript;
      });
      
      // Eager fetch: Load author data for all books with caching
      console.log(`[My Library] Fetching author data for ${enrichedData.length} books...`);
      
      // Try to load cached author data from sessionStorage
      const authorCache = {};
      try {
        const cachedAuthors = sessionStorage.getItem('newaudia_author_cache');
        if (cachedAuthors) {
          Object.assign(authorCache, JSON.parse(cachedAuthors));
          console.log(`[My Library] Loaded ${Object.keys(authorCache).length} authors from cache`);
        }
      } catch (e) {
        console.warn('[My Library] Failed to load author cache:', e);
      }
      
      const libraryWithAuthors = await Promise.all(
        enrichedData.map(async (item) => {
          try {
            // Use manuscriptId (camelCase) - this is what the backend returns
            const msId = item.manuscriptId;
            if (!msId) {
              console.warn(`[My Library] Missing manuscriptId for item:`, item.title);
              return { ...item, authorData: null };
            }
            
            // Check cache first
            if (authorCache[msId]) {
              console.log(`[My Library] Using cached author for ${item.title}`);
              return {
                ...item,
                authorData: authorCache[msId].author,
                authorType: authorCache[msId].authorType
              };
            }
            
            // Fetch if not in cache
            const authorResponse = await fetch(`${backendUrl}/api/public/manuscripts/${msId}/author`);
            const authorData = await authorResponse.json();
            const author = authorData.hasAuthorProfile ? authorData.author : null;
            const authorType = authorData.authorType || 'independent';
            
            // Store in cache (include authorType)
            if (author) {
              authorCache[msId] = { author, authorType };
            }
            
            return {
              ...item,
              authorData: author,
              authorType: authorType
            };
          } catch (error) {
            return { ...item, authorData: null, authorType: null };
          }
        })
      );
      
      // Save updated cache to sessionStorage
      try {
        sessionStorage.setItem('newaudia_author_cache', JSON.stringify(authorCache));
        console.log(`[My Library] Cached ${Object.keys(authorCache).length} authors`);
      } catch (e) {
        console.warn('[My Library] Failed to save author cache:', e);
      }
      
      console.log(`[My Library] Books with author data:`, libraryWithAuthors.filter(b => b.authorData).length);
      
      setLibrary(libraryWithAuthors);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching library:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch journey stats for "Your Journey So Far" card
  const fetchJourneyStats = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Fetch dashboard stats
      const dashboardResponse = await fetch(`${backendUrl}/api/reader/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!dashboardResponse.ok) {
        console.error('[Journey Stats] Failed to fetch dashboard data');
        return;
      }
      
      const dashboardData = await dashboardResponse.json();
      
      // Fetch full library to calculate top 3 titles by credits
      const libraryResponse = await fetch(`${backendUrl}/api/reader/library`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      let topTitles = [];
      if (libraryResponse.ok) {
        const libraryData = await libraryResponse.json();
        const books = libraryData.library || [];
        
        // Sort by credits (total) and take top 3
        topTitles = books
          .filter(book => (book.credits?.total ?? 0) > 0)
          .sort((a, b) => (b.credits?.total ?? 0) - (a.credits?.total ?? 0))
          .slice(0, 3)
          .map(book => ({
            title: book.title,
            credits: book.credits?.total ?? 0
          }));
      }
      
      setJourneyStats({
        activeTitles: dashboardData.stats?.total_books_in_library || 0,
        totalCredits: dashboardData.stats?.credits?.total || 0,
        totalAudiaQuestions: dashboardData.stats?.total_audia_questions || 0,
        topTitles: topTitles
      });
      
      console.log('[Journey Stats] Loaded:', {
        activeTitles: dashboardData.stats?.total_books_in_library,
        totalCredits: dashboardData.stats?.credits?.total,
        totalAudiaQuestions: dashboardData.stats?.total_audia_questions,
        topTitles: topTitles.length
      });
      
    } catch (err) {
      console.error('[Journey Stats] Error:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleManuscriptClick = async (manuscript) => {
    // CREDIT GATE: Block playback if balance is 0 or less (beta testers exempt)
    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
      console.log('[LIBRARY] BLOCKED: No credits remaining (balance:', balance, ')');
      notifyBilling('You need Reader Credits to listen. Please choose a plan to continue.');
      setShowCreditExpiredModal(true);
      return;
    }
    
    setSelectedManuscript(manuscript);
    
    // If no bookmark, open directly (first time playing)
    if (!manuscript.bookmark) {
      openManuscriptPlayer(manuscript.manuscriptId);
      return;
    }
    
    // Has bookmark - check if it's less than 24 hours old
    const bookmarkTimestamp = manuscript.bookmark?.timestamp || manuscript.lastInteraction;
    let hoursSinceBookmark = 999; // Default to >= 24 hours if no timestamp
    
    if (bookmarkTimestamp) {
      const lastInteractionTime = new Date(bookmarkTimestamp).getTime();
      const now = Date.now();
      hoursSinceBookmark = (now - lastInteractionTime) / (1000 * 60 * 60);
      console.log('[LIBRARY] Bookmark age:', hoursSinceBookmark.toFixed(2), 'hours');
    }
    
    // If bookmark < 24 hours old, go directly to player (skip "Bookmark Found" modal)
    if (hoursSinceBookmark < 24) {
      console.log('[LIBRARY] Bookmark < 24 hours - going directly to player');
      openManuscriptPlayer(manuscript.manuscriptId, {
        showRecapPrompt: false,
        startFromBeginning: false,
        bookmark: manuscript.bookmark
      });
      return;
    }
    
    // Bookmark >= 24 hours old - show the "Bookmark Found" resume modal
    console.log('[LIBRARY] Bookmark >= 24 hours - showing resume modal');
    setShowResumeModal(true);
  };

  // Handle Ask Audia button click with credit check
  const handleAskAudia = (manuscript) => {
    // CREDIT GATE: Block Ask Audia if balance is 0 or less (beta testers exempt)
    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
      console.log('[LIBRARY] Ask Audia BLOCKED: No credits remaining (balance:', balance, ')');
      notifyBilling('You need Reader Credits to use Ask Audia. Please choose a plan to continue.');
      setShowCreditExpiredModal(true);
      return;
    }
    
    setAudiaChatManuscript(manuscript);
    setShowAudiaChatModal(true);
  };

  const openManuscriptPlayer = async (manuscriptId, options = {}) => {
    try {
      const token = localStorage.getItem('accessToken');
      // Use apiFetch wrapper to avoid body stream issues from Emergent fetch interceptor
      const { apiFetch } = await import('../lib/apiFetch');
      
      const result = await apiFetch(`${backendUrl}/api/manuscripts/${manuscriptId}/open`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!result.ok) {
        // Log detailed error info for debugging
        const errorMsg = result.json?.detail?.message || result.json?.detail || result.error?.message || 'Failed to open manuscript';
        const traceId = result.traceId || result.json?.detail?.trace_id || result.json?.error?.trace_id;
        console.error(`[LIBRARY] /open failed: status=${result.status} trace_id=${traceId} error=${errorMsg}`, result);
        throw new Error(errorMsg);
      }
      
      const { session_id } = result.json;
      
      // Navigate to player with session_id and options
      navigate(`/player/${session_id}`, {
        state: {
          returnTo: '/reader/my-library',
          role: 'reader',
          showRecapPrompt: options.showRecapPrompt || false,
          startFromBeginning: options.startFromBeginning || false,
          bookmark: options.bookmark || null  // Pass bookmark data from database
        }
      });
    } catch (err) {
      console.error('Error opening manuscript:', err);
      setError(err.message || 'Failed to open manuscript player');
    }
  };

  const handleResume = () => {
    // CREDIT GATE: Block playback if balance is 0 or less (beta testers exempt)
    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
      console.log('[LIBRARY] BLOCKED on Resume: No credits remaining');
      notifyBilling('You need Reader Credits to listen. Please choose a plan to continue.');
      setShowResumeModal(false);
      setShowCreditExpiredModal(true);
      setSelectedManuscript(null);
      return;
    }
    
    if (selectedManuscript) {
      const bookmark = selectedManuscript.bookmark;
      console.log('[LIBRARY RESUME] Passing bookmark to player:', bookmark);
      console.log('[LIBRARY RESUME] Bookmark details - Chapter:', bookmark?.chapterIndex, 'Chunk:', bookmark?.chunkIndex, 'Seconds:', bookmark?.positionSeconds);
      
      // Check if we should show recap prompt (only if > 24 hours since last interaction)
      let shouldShowRecapPrompt = true; // Default to showing recap
      
      if (bookmark?.timestamp) {
        const lastInteractionTime = new Date(bookmark.timestamp).getTime();
        const now = Date.now();
        const hoursSinceLastInteraction = (now - lastInteractionTime) / (1000 * 60 * 60);
        
        console.log('[LIBRARY RESUME] Time check:', {
          lastInteraction: bookmark.timestamp,
          hoursSince: hoursSinceLastInteraction.toFixed(2),
          threshold: '24 hours'
        });
        
        // If less than 24 hours, skip recap and resume directly
        if (hoursSinceLastInteraction < 24) {
          console.log('[LIBRARY RESUME] Last interaction < 24 hours - skipping recap prompt');
          shouldShowRecapPrompt = false;
        } else {
          console.log('[LIBRARY RESUME] Last interaction > 24 hours - will show recap prompt');
        }
      } else {
        console.log('[LIBRARY RESUME] No timestamp on bookmark - will show recap prompt (legacy)');
      }
      
      setShowResumeModal(false);
      // Resume with recap prompt only if eligible (> 24 hours)
      openManuscriptPlayer(selectedManuscript.manuscriptId, { 
        showRecapPrompt: shouldShowRecapPrompt,
        bookmark: bookmark 
      });
      setSelectedManuscript(null);
    }
  };

  const handleStartOver = () => {
    // CREDIT GATE: Block playback if balance is 0 or less (beta testers exempt)
    if (!isBetaTester && typeof balance === 'number' && balance <= 0) {
      console.log('[LIBRARY] BLOCKED on Start Over: No credits remaining');
      notifyBilling('You need Reader Credits to listen. Please choose a plan to continue.');
      setShowResumeModal(false);
      setShowCreditExpiredModal(true);
      setSelectedManuscript(null);
      return;
    }
    
    if (selectedManuscript) {
      setShowResumeModal(false);
      // Start from beginning, no recap prompt
      openManuscriptPlayer(selectedManuscript.manuscriptId, { startFromBeginning: true });
      setSelectedManuscript(null);
    }
  };

  const handleCancel = () => {
    setShowResumeModal(false);
    setSelectedManuscript(null);
  };

  const handleShowDetails = async (manuscript) => {
    try {
      console.log('[Details Modal] Opening for manuscript:', manuscript.manuscriptId);
      console.log('[Details Modal] Credits from library:', manuscript.credits);
      
      // Fetch full manuscript details from backend
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL
      
      const response = await fetch(`${backendUrl}/api/manuscripts/${manuscript.manuscriptId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const fullManuscript = await response.json();
        
        // Get reader's personal stats from the current library data
        // (already includes wordsConsumed, minutesPlayed from reader_library)
        const wordsConsumed = 0; // Not currently in library response
        const minutesPlayed = 0; // Not currently in library response
        const progress = fullManuscript.canonical_word_count > 0 
          ? Math.round((wordsConsumed / fullManuscript.canonical_word_count) * 100)
          : 0;
        
        // Merge with reader's personal stats (including credits from library response)
        const mergedManuscript = {
          ...fullManuscript,
          coverImageUrl: `/api/covers/${manuscript.manuscriptId}?v=${fullManuscript?.cover_updated_at || fullManuscript?.updated_at || Date.now()}`,
          credits: manuscript.credits, // Include credits from library response
          isPromotional: manuscript.isPromotional, // Include promotional flag
          readerStats: {
            passiveEUs: manuscript.passiveEUs,
            activeEUs: manuscript.activeEUs,
            totalEUs: manuscript.passiveEUs + manuscript.activeEUs,
            wordsConsumed: wordsConsumed,
            minutesPlayed: minutesPlayed,
            progress: progress
          }
        };
        
        console.log('[Details Modal] Merged manuscript credits:', mergedManuscript.credits);
        setDetailsManuscript(mergedManuscript);
        setShowDetailsModal(true);
      }
    } catch (err) {
      console.error('Error fetching manuscript details:', err);
    }
  };

  const handleArchiveBook = (manuscript) => {
    setBookToArchive(manuscript);
    setShowArchiveModal(true);
  };

  const confirmArchive = async () => {
    if (!bookToArchive) return;

    try {
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL
      
      const response = await fetch(`${backendUrl}/api/reader/library/${bookToArchive.manuscriptId}/archive`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Close archive modal and show success
        setShowArchiveModal(false);
        setArchiveResult(result);
        setShowArchiveSuccessModal(true);
        
        // Refresh library to remove archived book
        fetchLibrary();
      } else {
        const error = await response.json();
        alert(`Failed to archive: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error archiving book:', err);
      alert('Failed to archive book. Please try again.');
    }
  };

  const handleUnarchiveBook = async (manuscript) => {
    try {
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL
      
      const response = await fetch(`${backendUrl}/api/reader/library/${manuscript.manuscriptId}/restore`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[UNARCHIVE] Book restored successfully:', result);
        
        // Switch back to active library view to show the restored book
        setShowArchived(false);
        
        // Refresh library (the useEffect will trigger automatically due to showArchived change)
      } else {
        const error = await response.json();
        alert(`Failed to restore: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error restoring book:', err);
      alert('Failed to restore book. Please try again.');
    }
  };

  // Show remove confirmation modal
  const handleRemoveClick = (manuscript) => {
    setBookToRemove(manuscript);
    setShowRemoveModal(true);
  };

  // Actually remove book from library after confirmation
  const confirmRemoveFromLibrary = async () => {
    if (!bookToRemove) return;
    
    const manuscript = bookToRemove;
    setShowRemoveModal(false);
    setBookToRemove(null);

    try {
      const token = localStorage.getItem('accessToken');
      // backendUrl imported from centralized config as BACKEND_URL
      
      const response = await fetch(`${backendUrl}/api/reader/library/${manuscript.manuscriptId}/remove`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh the library
        fetchLibrary();
      } else {
        const error = await response.json();
        console.error('Failed to remove:', error.detail || 'Unknown error');
      }
    } catch (err) {
      console.error('Error removing from library:', err);
    }
  };

  const formatChapterLabel = (chapterIndex) => {
    return `Chapter ${chapterIndex + 1}`;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate total Reader Credits across all books in library
  const totalReaderCredits = library.reduce((sum, book) => {
    return sum + (book.credits?.total ?? 0);
  }, 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 pb-20 md:pb-0 overflow-x-hidden">
      {/* Reader Mode Navigation - Logo, Credits, Buy Credits, Profile */}
      <ReaderNav />

      {/* Header */}
      <header className="bg-gradient-to-br from-indigo-600 via-purple-600 to-sky-500">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Left side - Welcome content */}
            <div className="flex-1">
              <p className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-1">
                NewAudia<sup className="text-lg sm:text-xl">™</sup>
              </p>
              <p className="text-lg sm:text-xl uppercase tracking-[0.2em] text-indigo-100">
                Your Library
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                Continue Your Journey
              </h1>
              <p className="mt-3 max-w-2xl text-sm sm:text-base text-indigo-100/90">
                Pick up where you left off or explore your collection of manuscripts. 
                Your progress and engagement are tracked automatically.
                <Link to="/help/library/managing-library" className="ml-2 text-indigo-200 hover:text-white underline underline-offset-2">
                  Learn more
                </Link>
              </p>
              {/* Reader Home & Show Archived Buttons */}
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <Link 
                  to="/home"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-medium transition-all"
                >
                  ← Reader Home
                </Link>
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                    showArchived 
                      ? 'bg-amber-500 text-white shadow-lg' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {showArchived ? '✓ Showing Archived' : 'Show Archived'}
                </button>
                <AskAudiaHelpButton context="my-library" />
              </div>
            </div>
            
            {/* Right side - Your Journey So Far Card */}
            <div className="w-full lg:w-auto flex-shrink-0">
              <div className="rounded-2xl bg-slate-900/90 backdrop-blur-sm border border-slate-700/50 p-5 shadow-xl">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Your Journey So Far
                </h2>
                
                {journeyStats ? (
                  <div className="flex gap-4">
                    {/* Left column: Stats in a 2x2 grid */}
                    <div className="grid grid-cols-2 gap-2 flex-shrink-0">
                      <div className="bg-slate-800 rounded-xl px-4 py-3 text-center min-w-[90px]">
                        <p className="text-2xl font-bold text-white">{journeyStats.activeTitles}</p>
                        <p className="text-[10px] text-slate-400 font-medium">Active Titles</p>
                      </div>
                      <div className="bg-slate-800 rounded-xl px-4 py-3 text-center min-w-[90px]">
                        <p className="text-2xl font-bold text-emerald-400">{journeyStats.totalCredits.toFixed(1)}</p>
                        <p className="text-[10px] text-slate-400 font-medium">Total RCs</p>
                      </div>
                      <div className="bg-slate-800 rounded-xl px-4 py-3 text-center col-span-2">
                        <p className="text-2xl font-bold text-purple-400">{journeyStats.totalAudiaQuestions}</p>
                        <p className="text-[10px] text-slate-400 font-medium">Ask Audia Questions</p>
                      </div>
                    </div>
                    
                    {/* Right column: Top 3 Titles - visually distinct */}
                    {journeyStats.topTitles.length > 0 && (
                      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border border-amber-500/30 min-w-[200px]">
                        <p className="text-[10px] text-amber-400 mb-3 font-bold uppercase tracking-wide flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                          Top 3 Titles by RCs
                        </p>
                        <div className="space-y-2">
                          {journeyStats.topTitles.map((title, index) => (
                            <div key={index} className="flex items-center justify-between text-xs gap-3">
                              <span className="text-slate-200 truncate flex-1" style={{ maxWidth: '150px' }}>
                                <span className="text-amber-400 font-bold mr-1.5">{index + 1}.</span>
                                {title.title}
                              </span>
                              <span className="text-emerald-400 font-bold whitespace-nowrap">
                                {title.credits.toFixed(1)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-8 sm:pt-10">
        {loading ? (
          // Loading Skeletons
          <div className="grid gap-6 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse rounded-3xl bg-slate-900/60 p-5 ring-1 ring-slate-800">
                <div className="flex gap-4">
                  <div className="h-40 w-28 sm:h-48 sm:w-32 rounded-lg bg-slate-800"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-4 w-3/4 rounded bg-slate-800"></div>
                    <div className="h-3 w-1/2 rounded bg-slate-800"></div>
                    <div className="h-3 w-full rounded bg-slate-800"></div>
                    <div className="h-8 w-32 rounded-full bg-slate-800 mt-4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          // Error State
          <div className="rounded-3xl bg-red-900/20 p-8 ring-1 ring-red-800">
            <h3 className="text-lg font-semibold text-red-400">Error Loading Library</h3>
            <p className="mt-2 text-sm text-slate-300">{error}</p>
            <button
              onClick={fetchLibrary}
              className="mt-4 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : library.length === 0 ? (
          // Empty State
          <div className="rounded-3xl bg-slate-900/60 p-12 text-center ring-1 ring-slate-800">
            <div className="mx-auto max-w-md">
              <svg className="mx-auto h-16 w-16 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="mt-6 text-xl font-semibold">Your library is empty</h3>
              <p className="mt-2 text-sm text-slate-400">
                Start listening to manuscripts to build your personal library. 
                Your progress and engagement will be tracked automatically.
              </p>
              <Link
                to="/home"
                className="mt-6 inline-flex items-center rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-900/40 transition hover:bg-indigo-500"
              >
                Browse Featured Titles
              </Link>
            </div>
          </div>
        ) : (
          // Library Grid
          <div className="grid gap-6 md:grid-cols-2">
            {library.map((manuscript) => (
              <article
                key={manuscript.manuscriptId}
                className="flex flex-col rounded-3xl bg-slate-900/60 p-4 ring-1 ring-slate-800 hover:ring-slate-700 transition-all relative"
              >
                <div className="flex gap-3">
                  {/* Cover Image - Clickable to Play */}
                  <div className="flex-shrink-0">
                    <div 
                      className="cursor-pointer group relative"
                      onClick={() => !showArchived && handleManuscriptClick(manuscript)}
                      title={showArchived ? "Unarchive to play" : "Click to play"}
                    >
                      {manuscript.coverImageUrl ? (
                        <>
                          <img
                            src={manuscript.coverImageUrl}
                            alt={manuscript.title}
                            className="h-40 w-28 sm:h-48 sm:w-32 rounded-lg object-cover ring-1 ring-slate-700 transition-transform group-hover:scale-105"
                          />
                          {/* Play Overlay on Hover */}
                          {!showArchived && (
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg flex items-center justify-center transition-all pointer-events-none">
                              <svg className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex h-40 w-28 sm:h-48 sm:w-32 items-center justify-center rounded-lg bg-slate-800 ring-1 ring-slate-700">
                          <svg className="h-12 w-12 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Manuscript Info */}
                  <div className="flex flex-1 flex-col min-w-0">
                    {/* Title & Author */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-semibold text-slate-50 line-clamp-2 leading-tight">
                          {manuscript.title}
                        </h3>
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          by {manuscript.author}
                        </p>
                      </div>
                      {showArchived && (
                        <span className="rounded-full bg-amber-900/40 px-2 py-0.5 text-[10px] font-semibold text-amber-200 border border-amber-800/50">
                          ARCHIVED
                        </span>
                      )}
                    </div>
                    
                    {/* Credits + Play/Continue + Ask Audia - Row with larger elements */}
                    <div className="mt-3 flex items-stretch gap-2">
                      {/* Credits Used Display */}
                      {manuscript.isExempt ? (
                        <div className="rounded-xl bg-slate-800/50 px-3 py-2 text-xs text-slate-400 italic flex items-center">
                          No credits
                        </div>
                      ) : manuscript.isPromotional ? (
                        <div className="rounded-xl bg-gradient-to-r from-green-900/40 to-emerald-900/40 px-4 py-2 border border-green-800/30 flex flex-col justify-center">
                          <p className="text-xs text-green-400 font-bold">FREE</p>
                        </div>
                      ) : (
                        <div className="rounded-xl bg-gradient-to-r from-indigo-900/40 to-purple-900/40 px-3 py-2 text-center flex flex-col justify-center min-w-[75px]">
                          <p className="text-[10px] text-indigo-300/70 leading-none">Credits Used</p>
                          <p className="text-lg font-bold text-white leading-tight">{(manuscript.credits?.total ?? 0).toFixed(1)}</p>
                        </div>
                      )}
                      
                      {/* Play/Continue Button */}
                      <button
                        onClick={() => handleManuscriptClick(manuscript)}
                        disabled={showArchived}
                        className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold shadow-md transition flex items-center justify-center gap-2 ${
                          showArchived
                            ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-50'
                            : 'bg-indigo-600 text-white shadow-indigo-900/40 hover:bg-indigo-500'
                        }`}
                        title={showArchived ? "Unarchive this book to play" : (manuscript.bookmark ? "Continue listening" : "Play this book")}
                        data-testid={`play-btn-${manuscript.manuscriptId}`}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                        {showArchived ? 'Archived' : (manuscript.bookmark ? <span className="hidden sm:inline">Continue</span> : <span className="hidden sm:inline">Play</span>)}
                      </button>
                      
                      {/* Ask Audia Button */}
                      <button
                        onClick={() => handleAskAudia(manuscript)}
                        disabled={showArchived}
                        className={`rounded-xl px-3 py-2 transition flex items-center gap-2 ${
                          showArchived
                            ? 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-50'
                            : 'bg-gradient-to-br from-purple-600 via-pink-600 to-cyan-500 text-white hover:from-purple-500 hover:via-pink-500 hover:to-cyan-400'
                        }`}
                        title="Hi, I'm Audia. Click here to ask me anything about this title without having to open the book."
                      >
                        <img 
                          src="/audia-avatar.jpg" 
                          alt="Audia" 
                          className="w-7 h-7 rounded-full object-cover border-2 border-white/60"
                        />
                        <span className="text-xs font-semibold hidden sm:inline">Ask Audia</span>
                      </button>
                    </div>

                    {/* Bookmark Info - HIDDEN but data still used for resume functionality */}
                    {manuscript.bookmark && (
                      <div className="hidden">
                        {/* Bookmark data: Chapter {manuscript.bookmark.chapterIndex + 1}, Chunk {manuscript.bookmark.chunkIndex}, {manuscript.bookmark.positionSeconds?.toFixed(1)}s */}
                      </div>
                    )}

                    {/* Action Buttons Row - Full width */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {/* Order Paperback */}
                      {manuscript.purchaseUrl && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(manuscript.purchaseUrl, '_blank', 'noopener,noreferrer');
                          }}
                          className="flex-1 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500 transition-all shadow-md"
                          title="Purchase the physical book"
                        >
                          <svg className="h-3.5 w-3.5 hidden sm:inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          <ShoppingCart size={15} className="inline sm:hidden" />
                          <span className="hidden sm:inline">Paperback</span>
                        </button>
                      )}
                      
                      {/* Details Button */}
                      <button
                        onClick={() => handleShowDetails(manuscript)}
                        className="flex-1 rounded-xl px-3 py-2 text-xs font-semibold bg-slate-700 text-slate-200 hover:bg-slate-600 transition-all"
                      >
                        <Info size={15} className="inline sm:hidden" />
                        <span className="hidden sm:inline">Details</span>
                      </button>
                      
                      {/* About Author Button */}
                      {manuscript.authorData && (
                        <button
                          onClick={() => {
                            if (manuscript.authorType === 'managed') {
                              setSelectedManagedAuthorId(manuscript.authorData.managed_author_id || manuscript.authorData.author_id);
                              setShowManagedAuthorModal(true);
                            } else {
                              setSelectedAuthor(manuscript.authorData);
                              setShowAuthorModal(true);
                            }
                          }}
                          className="flex-1 rounded-xl px-3 py-2 text-xs font-semibold bg-purple-900/40 text-purple-200 hover:bg-purple-900/60 border border-purple-800/50 transition"
                          title="Learn about the author"
                        >
                          <User size={15} className="inline sm:hidden" />
                          <span className="hidden sm:inline">Author</span>
                        </button>
                      )}
                      
                      {/* Archive / Unarchive Button */}
                      {showArchived ? (
                        <button
                          onClick={() => handleUnarchiveBook(manuscript)}
                          className="flex-1 rounded-xl px-3 py-2 text-xs font-semibold bg-green-900/40 text-green-200 hover:bg-green-900/60 border border-green-800/50 transition"
                          title="Restore this book to your active library"
                        >
                          <ArchiveRestore size={15} className="inline sm:hidden" />
                          <span className="hidden sm:inline">Unarchive</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleArchiveBook(manuscript)}
                          className="flex-1 rounded-xl px-3 py-2 text-xs font-semibold bg-amber-900/40 text-amber-200 hover:bg-amber-900/60 border border-amber-800/50 transition"
                          title="Archive this book (preserves all reading history)"
                        >
                          <Archive size={15} className="inline sm:hidden" />
                          <span className="hidden sm:inline">Archive</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* Resume Modal */}
      <AlertDialog open={showResumeModal} onOpenChange={setShowResumeModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            {/* NewAudia Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-12 object-contain"
              />
            </div>
            
            <AlertDialogTitle className="text-2xl font-bold text-center text-cyan-400">
              Bookmark Found
            </AlertDialogTitle>
          </AlertDialogHeader>
          
          {/* Body */}
          <div className="p-6 space-y-4">
            <div className="flex flex-col gap-2">
              <button
                onClick={handleResume}
                className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-semibold transition-colors text-lg"
              >
                RESUME
              </button>
              <button
                onClick={handleStartOver}
                className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl font-semibold transition-colors text-lg"
              >
                START OVER
              </button>
              <button
                onClick={handleCancel}
                className="w-full px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-semibold transition-colors text-lg"
              >
                CANCEL
              </button>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Book Details Modal */}
      <AlertDialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <AlertDialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            {/* NewAudia Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-12 object-contain"
              />
            </div>
            
            <div className="flex justify-between items-start mb-4">
              <AlertDialogTitle className="text-xl font-bold">Book Details</AlertDialogTitle>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <AlertDialogDescription className="sr-only">View details of the selected book</AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Book Details Content - Outside AlertDialogDescription to avoid p>div nesting issues */}
          {detailsManuscript && (
            <div className="space-y-4">
              {/* Cover Image */}
              {detailsManuscript.coverImageUrl && (
                <div className="flex justify-center">
                  <img 
                    src={detailsManuscript.coverImageUrl}
                    alt={detailsManuscript.title}
                    className="h-48 w-auto rounded-lg shadow-lg object-cover"
                  />
                </div>
              )}
              
              {/* Title and Author */}
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900">{detailsManuscript.title}</h2>
                {detailsManuscript.subtitle && (
                  <p className="text-sm text-gray-600 mt-1">{detailsManuscript.subtitle}</p>
                )}
                <p className="text-base text-gray-700 mt-2">by {detailsManuscript.author}</p>
              </div>
              
              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {detailsManuscript.publisher && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 font-semibold uppercase">Publisher</p>
                    <p className="text-gray-900 mt-1">{detailsManuscript.publisher}</p>
                  </div>
                )}
                
                {detailsManuscript.chapter_count && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 font-semibold uppercase">Chapters</p>
                    <p className="text-gray-900 mt-1">{detailsManuscript.chapter_count}</p>
                  </div>
                )}
                
                {detailsManuscript.canonical_word_count && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 font-semibold uppercase">Total Words</p>
                    <p className="text-gray-900 mt-1">{detailsManuscript.canonical_word_count.toLocaleString()}</p>
                  </div>
                )}
                
                {detailsManuscript.genre && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 font-semibold uppercase">Genre</p>
                    <p className="text-gray-900 mt-1 capitalize">{detailsManuscript.genre}</p>
                  </div>
                )}
                
                {detailsManuscript.theme && (
                  <div className="col-span-2 bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 font-semibold uppercase">Theme</p>
                    <p className="text-gray-900 mt-1 capitalize">{detailsManuscript.theme}</p>
                  </div>
                )}
              </div>
              
              {/* Reader's Personal Stats */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Reading Progress</h3>
                
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">
                    {(detailsManuscript.credits?.total ?? 0).toFixed(1)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Reader Credits</p>
                </div>
              </div>
              
              {/* Order Paperback Button */}
              {detailsManuscript.purchase_url && (
                <div className="border-t border-gray-200 pt-4">
                  <button
                    onClick={() => window.open(detailsManuscript.purchase_url, '_blank', 'noopener,noreferrer')}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-purple-500 hover:to-indigo-500 transition-all shadow-md hover:shadow-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    Order Paperback
                  </button>
                </div>
              )}
            </div>
          )}
          
          <div className="mt-4">
            <button
              onClick={() => setShowDetailsModal(false)}
              className="w-full px-4 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors font-semibold"
            >
              Close
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation Modal */}
      <AlertDialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            {/* NewAudia Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-12 object-contain"
              />
            </div>
            
            <AlertDialogTitle className="flex items-center justify-center gap-2 text-xl">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              Archive Book
            </AlertDialogTitle>
            
            <AlertDialogDescription className="space-y-4">
              <p className="text-gray-700 text-center font-semibold">
                Archive "{bookToArchive?.title}"?
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                <p className="text-sm text-gray-700">
                  <strong>What happens:</strong>
                </p>
                <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                  <li>Book will be hidden from your active library</li>
                  <li>All reading history and progress preserved</li>
                  <li>You can restore it anytime</li>
                </ul>
              </div>
              
              <p className="text-xs text-gray-500 italic text-center">
                💡 Use "Show Archived" to view archived books
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              onClick={() => setShowArchiveModal(false)}
              className="px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={confirmArchive}
              className="px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-semibold"
            >
              Archive
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Success Modal */}
      <AlertDialog open={showArchiveSuccessModal} onOpenChange={setShowArchiveSuccessModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            {/* NewAudia Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/newaudia-logo.jpg" 
                alt="NewAudia" 
                className="h-12 object-contain"
              />
            </div>
            
            <AlertDialogTitle className="flex items-center justify-center gap-2 text-xl text-green-700">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Book Archived Successfully
            </AlertDialogTitle>
            
            <AlertDialogDescription className="space-y-4">
              <p className="text-gray-700 text-center">
                "{bookToArchive?.title}" has been archived.
              </p>
              
              {archiveResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-700">
                    ✅ Reading History Preserved:
                  </p>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div className="flex justify-between">
                      <span>Engagement Units:</span>
                      <span className="font-semibold">{archiveResult.total_engagement_units?.toFixed(1) || 0} EUs</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Listening Time:</span>
                      <span className="font-semibold">{Math.round((archiveResult.total_listen_seconds || 0) / 60)} minutes</span>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500 italic text-center">
                💡 Click "Show Archived" to view and restore this book
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="mt-4">
            <button
              onClick={() => {
                setShowArchiveSuccessModal(false);
                setBookToArchive(null);
                setArchiveResult(null);
              }}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
            >
              Done
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Confirmation Modal */}
      <AlertDialog open={showRemoveModal} onOpenChange={setShowRemoveModal}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center justify-center gap-2 text-xl text-red-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Remove from Library?
            </AlertDialogTitle>

            <AlertDialogDescription className="space-y-4">
              {bookToRemove && (
                <div className="text-center">
                  <p className="text-slate-600">
                    Remove <span className="font-semibold">"{bookToRemove.title}"</span> from your library?
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    You can add it back anytime from the public library.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex gap-3 mt-4">
            <button
              onClick={() => {
                setShowRemoveModal(false);
                setBookToRemove(null);
              }}
              className="flex-1 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition font-medium"
            >
              Cancel
            </button>
            <button
              onClick={confirmRemoveFromLibrary}
              className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition font-medium"
            >
              Remove
            </button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Author Profile Modal - For independent authors */}
      {selectedAuthor && (
        <AuthorProfileModal
          isOpen={showAuthorModal}
          onClose={() => {
            setShowAuthorModal(false);
            setSelectedAuthor(null);
          }}
          author={selectedAuthor}
        />
      )}

      {/* About Author Modal - For managed/publisher authors */}
      <AboutAuthorModal
        isOpen={showManagedAuthorModal}
        onClose={() => {
          setShowManagedAuthorModal(false);
          setSelectedManagedAuthorId(null);
        }}
        authorId={selectedManagedAuthorId}
        authorType="managed"
        onSelectBook={handleSelectBookFromAuthor}
      />

      {/* About Book Modal - For books selected from author's other titles */}
      <AboutBookModal
        isOpen={showAboutBookModal}
        onClose={() => {
          setShowAboutBookModal(false);
          setAboutBookData(null);
        }}
        book={aboutBookData}
      />

      {/* Ask Audia Chat Modal */}
      <AudiaChatModal
        isOpen={showAudiaChatModal}
        onClose={() => {
          setShowAudiaChatModal(false);
          setAudiaChatManuscript(null);
        }}
        manuscriptId={audiaChatManuscript?.manuscriptId}
        manuscriptTitle={audiaChatManuscript?.title}
        currentUser={user}
      />

      <UniversalFooter />
      <ReaderMobileBottomNav />
    </div>
  );
};

export default ReaderLibraryPage;
